export type SnippetPlatform = 'custom' | 'shopify' | 'woocommerce';

export type SnippetName = 'pdp' | 'all-variants' | 'card' | 'matrix' | 'price' | 'quick-buy';

type SnippetSpec = {
  /** Fills the comment: "SparkLayer {description} script". */
  description: string;
  element: string;
  /** Bare attribute switching the element into a mode, where one applies. */
  modifier?: string;
  /** Shopify keeps these in fixed folders, so its snippets lead with the location. */
  directory: '/sections/' | '/snippets/';
};

const SNIPPETS: Record<SnippetName, SnippetSpec> = {
  pdp: {
    description: 'product detail page',
    element: 'spark-pdp',
    directory: '/sections/',
  },
  'all-variants': {
    description: 'product detail page',
    element: 'spark-pdp',
    modifier: 'mode-all-variants',
    directory: '/sections/',
  },
  card: {
    description: 'product card',
    element: 'spark-product-card',
    directory: '/snippets/',
  },
  matrix: {
    description: 'product matrix',
    element: 'spark-product-matrix',
    directory: '/sections/',
  },
  price: {
    description: 'product price',
    element: 'spark-product-price',
    directory: '/snippets/',
  },
  'quick-buy': {
    description: 'product quick buy',
    element: 'spark-product-card',
    modifier: 'mode-quick-buy',
    directory: '/snippets/',
  },
};

/** How each platform names the product being rendered. */
const PARENT_ID: Record<SnippetPlatform, string> = {
  custom: '{{ id }}',
  shopify: '{{ product.id }}',
  woocommerce: '<?php echo absint( $product->get_id() ); ?>',
};

/**
 * `custom/all-variants` and `custom/matrix` kept Shopify's Liquid `product.id` when they were
 * copied from it, and `all-variants` kept its `/sections/` hint too. Reproduced so the output
 * is unchanged — they look like copy-paste slips, but confirming that is a content fix.
 */
const CUSTOM_OVERRIDES: Partial<
  Record<SnippetName, { parentId?: string; showDirectory?: boolean }>
> = {
  'all-variants': { parentId: PARENT_ID.shopify, showDirectory: true },
  matrix: { parentId: PARENT_ID.shopify },
};

const B2C_ONLY_EXAMPLE = `<!-- Hide any element by adding data-spark="b2c-only" to it, e.g. -->
<div data-spark="b2c-only">
    <!-- This would hide anything inside-->
</div>`;

function buildSnippet(platform: SnippetPlatform, name: SnippetName): string {
  const spec = SNIPPETS[name];
  const override = platform === 'custom' ? CUSTOM_OVERRIDES[name] : undefined;

  const parentId = override?.parentId ?? PARENT_ID[platform];
  const showDirectory = platform === 'shopify' || override?.showDirectory === true;
  const attributes = [spec.modifier, `parent-id="${parentId}"`].filter(Boolean).join(' ');

  const lines = [
    ...(showDirectory ? [`<!-- File typically in ${spec.directory} directory-->`] : []),
    `<!-- SparkLayer ${spec.description} script-->`,
    `<${spec.element} ${attributes}></${spec.element}>`,
    B2C_ONLY_EXAMPLE,
  ];

  return `${lines.join('\n')}\n`;
}

export function tagSnippet(platform: SnippetPlatform, name: SnippetName): string {
  return buildSnippet(platform, name);
}
